<script setup>
import { ref, computed, nextTick, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { verifySignupCode, resendSignupCode } from '../../../api/auth';

const props = defineProps({ email: { type: String, default: '' } });
const { t } = useI18n();
const router = useRouter();
const store = useStore();
const globalConfig = computed(() => store.getters['globalConfig/get']);
let saved = {};
try {
  saved = JSON.parse(sessionStorage.getItem('signupVerification') || '{}');
} catch {
  sessionStorage.removeItem('signupVerification');
}
const email = saved.email || props.email;
const verificationId = saved.verification_id;
if (!verificationId) router.replace({ name: 'auth_signup' });

// Cached challenges from before the update had six digits and no length metadata.
const codeLength = ref(saved.code_length === 8 ? 8 : 6);
const digits = ref(Array(codeLength.value).fill(''));
const inputRefs = ref([]);
const code = computed(() => digits.value.join(''));
const canSubmit = computed(() => code.value.length === codeLength.value);
const error = ref('');
const notice = ref('');
const busy = ref(false);
const remaining = ref(
  Math.max(0, Math.ceil(((saved.resendAt || 0) - Date.now()) / 1000))
);
const timer = setInterval(() => {
  if (remaining.value > 0) remaining.value -= 1;
}, 1000);
onUnmounted(() => clearInterval(timer));

const focusInput = index => {
  inputRefs.value[Math.max(0, Math.min(index, codeLength.value - 1))]?.focus();
};
const enterDigits = async (value, index) => {
  const normalized = value
    .replace(/[\u0660-\u0669]/g, digit => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, digit => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/\D/g, '');
  const start = normalized.length >= codeLength.value ? 0 : index;
  const incoming = normalized.slice(0, codeLength.value - start);
  if (!incoming) digits.value[index] = '';
  incoming.split('').forEach((digit, offset) => {
    digits.value[start + offset] = digit;
  });
  await nextTick();
  if (incoming) focusInput(start + incoming.length);
};
const handleInput = (event, index) => {
  enterDigits(event.target.value, index);
  event.target.value = digits.value[index];
};
const handleKeydown = (event, index) => {
  if (event.key === 'Backspace') {
    event.preventDefault();
    const target = digits.value[index] || index === 0 ? index : index - 1;
    digits.value[target] = '';
    focusInput(target);
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    event.preventDefault();
    focusInput(index + (event.key === 'ArrowLeft' ? -1 : 1));
  }
};

const verify = async () => {
  if (busy.value || !canSubmit.value) return;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await verifySignupCode({
      verification_id: verificationId,
      otp_code: code.value,
    });
    sessionStorage.removeItem('signupVerification');
    window.location.assign(data.redirect_url);
  } catch (e) {
    error.value = e.response?.data?.message || t('REGISTER.OTP.ERROR');
  } finally {
    busy.value = false;
  }
};
const resend = async () => {
  if (busy.value || remaining.value) return;
  busy.value = true;
  error.value = '';
  notice.value = '';
  try {
    const { data } = await resendSignupCode(verificationId);
    remaining.value = data.resend_after;
    sessionStorage.setItem(
      'signupVerification',
      JSON.stringify({
        ...data,
        resendAt: Date.now() + data.resend_after * 1000,
      })
    );
    codeLength.value = data.code_length === 8 ? 8 : 6;
    digits.value = Array(codeLength.value).fill('');
    await nextTick();
    focusInput(0);
    notice.value = t('REGISTER.OTP.RESENT');
  } catch (e) {
    error.value = e.response?.data?.message || t('REGISTER.OTP.ERROR');
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <main
    class="flex items-center justify-center min-h-screen bg-n-background p-5"
  >
    <section
      class="w-full max-w-md rounded-2xl bg-n-solid-1 border border-n-weak p-6 sm:p-8"
    >
      <img
        :src="globalConfig.logo"
        :alt="globalConfig.installationName"
        class="w-16 mb-8"
      />
      <span
        class="i-lucide-mail-check size-8 text-n-blue-10"
        aria-hidden="true"
      />
      <h1 class="text-2xl font-semibold text-n-slate-12 mt-4">
        {{ t('REGISTER.OTP.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11 mt-2 mb-6">
        {{ t('REGISTER.OTP.DESCRIPTION', { email, count: codeLength }) }}
      </p>
      <form class="space-y-4" @submit.prevent="verify">
        <fieldset class="min-w-0 border-0 p-0" :disabled="busy">
          <legend class="mb-2 text-sm text-n-slate-12">
            {{ t('REGISTER.OTP.LABEL') }}
          </legend>
          <div
            dir="ltr"
            class="grid gap-1 sm:gap-2"
            :class="codeLength === 8 ? 'grid-cols-8' : 'grid-cols-6'"
          >
            <input
              v-for="(digit, index) in digits"
              :id="`signup-code-${index + 1}`"
              :key="index"
              ref="inputRefs"
              :value="digit"
              type="text"
              :autofocus="index === 0"
              inputmode="numeric"
              :autocomplete="index === 0 ? 'one-time-code' : 'off'"
              pattern="[0-9]"
              :maxlength="codeLength"
              :aria-label="
                t('REGISTER.OTP.DIGIT', {
                  number: index + 1,
                  count: codeLength,
                })
              "
              :aria-invalid="Boolean(error)"
              aria-describedby="signup-code-hint signup-code-error"
              required
              class="w-full min-w-0 h-11 rounded-lg border border-n-strong bg-n-background p-0 text-xl text-center text-n-slate-12 focus:ring-2 focus:ring-n-blue-9"
              @focus="$event.target.select()"
              @input="handleInput($event, index)"
              @keydown="handleKeydown($event, index)"
              @paste.prevent="
                enterDigits($event.clipboardData.getData('text'), index)
              "
            />
          </div>
        </fieldset>
        <p
          v-if="error"
          id="signup-code-error"
          role="alert"
          class="text-sm text-n-ruby-11"
        >
          {{ error }}
        </p>
        <p v-if="notice" role="status" class="text-sm text-n-teal-11">
          {{ notice }}
        </p>
        <NextButton
          type="submit"
          lg
          class="w-full"
          :label="t('REGISTER.OTP.VERIFY')"
          :disabled="busy || !canSubmit"
          :is-loading="busy"
        />
      </form>
      <p id="signup-code-hint" class="text-xs text-n-slate-10 mt-4">
        {{ t('REGISTER.OTP.EXPIRY') }}
      </p>
      <NextButton
        class="w-full mt-4"
        ghost
        slate
        :disabled="busy || remaining > 0"
        :label="
          remaining
            ? t('REGISTER.OTP.RESEND_AFTER', { seconds: remaining })
            : t('REGISTER.OTP.RESEND')
        "
        @click="resend"
      />
      <router-link
        :to="{ name: 'auth_signup' }"
        class="block text-center text-sm text-n-blue-11 mt-4"
      >
        {{ t('REGISTER.OTP.CHANGE_EMAIL') }}
      </router-link>
    </section>
  </main>
</template>
